import React, { useCallback, useEffect, useState } from 'react';

import {
  Button,
  Label,
  Spinner,
  ListerItem,
  Notification,
  ScrollableContainer,
  SearchInputToggle,
} from '@ergeon/core-components';
import { PhoneArrowUpRightIcon } from '@ergeon/icons/hero/micro';

import { getAgents } from '../../api';
import { Agent } from '../../types';
import { formatPhoneNumber } from '../Customers/utils/formatUtils';

type Props = {
  excludeIdentity?: string;
  onSelect: (agent: Agent) => void;
  title?: string;
};

const AgentSelectionView: React.FC<Props> = ({ excludeIdentity, onSelect, title = 'Select Agent' }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();

  const fetchAgents = useCallback(async(excludeIdentity?: string) => {
    try {
      const agents = await getAgents(excludeIdentity);
      setAgents(agents);
      setError(undefined);
    } catch (e) {
      console.error(e);
      setError('We were not able to load active agents, please try again later');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchAgents(excludeIdentity);
  }, [fetchAgents, excludeIdentity]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = agents
        .filter(
          (agent) =>
            agent.identity.toLowerCase().includes(searchTerm.toLowerCase()) ||
            agent.phone_number.toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .slice(0, 10);
      setFilteredAgents(filtered);
    } else {
      setFilteredAgents(agents.slice(0, 10));
    }
  }, [searchTerm, agents]);

  const getAgentItems = (agent: Agent) => {
    return [
      {
        title: 'Agent:',
        value: agent.identity,
      },
      {
        title: 'Phone number:',
        value: formatPhoneNumber(agent.phone_number),
      },
    ];
  };

  const getAgentButtons = (agent: Agent) => {
    return [
      <Button
        aria-label={`Select agent ${agent.identity}`}
        className="whitespace-nowrap"
        flavor="success"
        key="select-agent-button"
        onClick={() => onSelect(agent)}
        size="small"
        taste="line"
        title={`Select agent ${agent.identity}`}>
        <PhoneArrowUpRightIcon className="size-4 shrink-0" />
        Call
      </Button>,
    ];
  };

  const getNotificationContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col justify-center items-center gap-2 p-4 dark:text-white paragraph-2">
          <Spinner active borderWidth={0.2} color="blue" size={32} />
          <p className="text-gray-700 dark:text-gray-400 paragraph-2">Loading agents list...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Notification mode={'embed'} type={'Error'}>
          {error}
        </Notification>
      );
    }

    if (!isLoading && agents.length === 0) {
      return (
        <Notification mode={'embed'} type={'Information'}>
          There are no agents online besides you.
        </Notification>
      );
    }

    if (!isLoading && filteredAgents.length === 0 && searchTerm) {
      return (
        <Notification mode={'embed'} type={'Information'}>
          No agents match your search.
        </Notification>
      );
    }

    return null;
  };

  const getAgentsList = () => {
    if (filteredAgents.length === 0) return null;

    return (
      <div className="flex flex-col py-2 h-96">
        <ScrollableContainer direction="col" gap={1}>
          {filteredAgents.map((agent) => {
            return <ListerItem buttons={getAgentButtons(agent)} items={getAgentItems(agent)} key={agent.identity} />;
          })}
        </ScrollableContainer>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <SearchInputToggle
        isLoading={false}
        isToggleEnabled={false}
        onChange={(value) => setSearchTerm(value || '')}
        onSubmit={() => {
          /* no-op */
        }}
        onToggleClick={() => {
          /* no-op */
        }}
        placeholder="Search agents..."
        showSubmitButton={false}
        showToggle={false}
        value={searchTerm} />
      <div className="flex flex-col gap-2">
        <Label>{title}:</Label>
        {getNotificationContent()}
        {getAgentsList()}
      </div>
    </div>
  );
};

export default AgentSelectionView;
